/**
 * 股數解析與驗證(FIN-06)
 *
 * 取代各表單的 parseInt 驗證 — parseInt 的問題:
 *  - parseInt("abc") → NaN,而 NaN <= 0 為 false,可以通過「不正確」的驗證
 *  - parseInt("1e3") → 1,type=number 輸入科學記號會把 1000 股記成 1 股
 *  - 小數股數被靜默截斷
 */

/**
 * 解析股數字串
 * @returns 有效股數;無效時回傳 null
 *
 * 規則:
 *  - 必須是有限數字
 *  - 台股以「股」為單位,一般為正整數;零股交易支援 1 股為最小單位(整數)
 */
export function parseQuantity(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }
  if (parsed <= 0) {
    return null;
  }
  if (!Number.isInteger(parsed)) {
    return null;
  }

  return parsed;
}

/**
 * 解析價格字串(允許小數)
 * @param maxDecimalPlaces 最大允許的小數位數(買賣:台股最小跳動單位 0.01 → 2 位)
 * @returns 有效價格;無效時回傳 null
 */
export function parsePrice(
  value: string | number | null | undefined,
  maxDecimalPlaces: number = 2,
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }
  if (parsed <= 0) {
    return null;
  }
  // 限制小數位數(超過允許位數的小數拒絕;1e-2 等可解析為合法值的科學記號會通過)
  const factor = 10 ** maxDecimalPlaces;
  if (Math.round(parsed * factor) / factor !== parsed) {
    return null;
  }

  return parsed;
}

/**
 * 依交易類型取得價格欄允許的最大小數位數
 *
 * - 買賣:台股最小跳動單位 0.01 → 2 位
 * - 股利:台灣公司公告的每股現金股利可達小數 8 位(如台積電 3.49978969),
 *   實測 FinMind 2015 年以來資料約 25% 的記錄超過 2 位小數,必須保留完整精度,
 *   總額由 DecimalHelper 以「每股 × 股數後捨去到整數元」計算,不因進位失真
 */
export function maxPriceDecimals(type: string): number {
  return type === 'dividend' ? 8 : 2;
}

/**
 * 交易表單的價格欄位驗證(ManualTrading 與 EditTransactionDialog 共用)
 *
 * 語意:
 *  - 一般交易:必須是 > 0 的有效價格
 *  - 股票異動(stock_increase/stock_decrease):價格無意義,可留空或填 0,
 *    但不得是無法解析的字串(避免垃圾輸入被靜默當成 0 儲存)
 *
 * @returns 錯誤訊息;通過驗證時回傳 null
 */
export function validateTransactionPrice(
  rawPrice: string | null | undefined,
  type: string,
): string | null {
  const isStockAdjustment = type === 'stock_increase' || type === 'stock_decrease';
  const price = parsePrice(rawPrice || '0', maxPriceDecimals(type));

  if (price === null) {
    if (!isStockAdjustment) {
      return '價格必須為數字且不能為負數';
    }
    const raw = rawPrice ?? '';
    if (raw !== '' && !Number.isFinite(Number(raw))) {
      return '價格欄位必須是數字（股票異動可填 0 或留空）';
    }
    return null; // 異動 + 空值/0 → 合法
  }

  if (!isStockAdjustment && price <= 0) {
    return '請輸入有效的價格（股票異動除外）';
  }

  return null;
}
